/* Maison Mata — prescription lens configurator.
   Prices are read live from Shopify product JSON; only the rules live here.
   Rewritten 2026-09-07. Key invariants:
     - the drawer is moved to <body> so position:fixed is never trapped by a
       transformed ancestor;
     - every control is delegated, so a re-render can never leave a dead button;
     - opening is wrapped so the page scroll lock is always released on error;
     - plan() is the single source of truth: what we quote is what we charge.
*/
(function () {
  "use strict";
  var VERSION = "2026-09-07b";
  window.MM_RX_VERSION = VERSION;

  var CFG = {
    handles: {
      indo: "single-vision-lens",
      euro: "single-vision-lens-european",
      add: "single-vision-lens-additinional-slimmer-lens-indo-european",
      plano: "plano-lens"
    },
    // Confirmed thresholds, on the sphere, either sign:
    //   < 4.00  standard, Slim optional for comfort
    //   4.00    Slim mandatory
    //   4.50    Super Slim mandatory
    //   6.00    appointment in store, not sold online
    slimAdvisedFrom: 1.75,
    slimFrom: 4.0,
    superFrom: 4.5,
    ultraFrom: 6.0,
    // 6.00 itself is still sold online, on an Ultra Slim. Past it we measure
    // on the face, in store, like the progressives.
    storeOnlyFrom: 6.0,
    bandMax: 2.0,
    lens: {
      ar: {
        indo: { stock: "Anti-Reflective", order: "Anti-Reflective Semi Finish" },
        euro: { stock: "Anti-Reflective", order: "Anti-Reflective Semi Finish" }
      },
      bl: {
        indo: { stock: "Bluelight", order: "Blue Light Semi Finish" },
        euro: { stock: "Bluelight Clear", order: "Blue Light Semi Finish" }
      },
      photo: {
        indo: { stock: "Photochromic", order: "Photochromic Semi Finish" },
        euro: { stock: "Photochromic Grey or Brown", order: "Photochromic Semi Finish" }
      }
    },
    thick: {
      slim: "Slim and light 1.6",
      "super": "Ultra Slim and Light 1.67",
      ultra: "Maximum Slim and Light 1.74"
    },
    // Without a prescription there is one sensible lens, the blue light
    // filter, included with the frame. Photochromic without correction was
    // dropped on purpose: nobody buys it.
    plano: { bl: "Bluelight" },
    avail: {
      ar: {
        indo: { stock: ["std", "slim", "super"], order: ["std", "slim", "super"] },
        euro: { stock: ["std", "slim", "super", "ultra"], order: ["std", "slim", "super", "ultra"] }
      },
      bl: {
        indo: { stock: ["std", "slim", "super"], order: ["std", "slim", "super"] },
        euro: { stock: ["std", "slim", "super", "ultra"], order: ["std", "slim", "super", "ultra"] }
      },
      photo: {
        indo: { stock: ["std"], order: ["std", "slim", "super"] },
        euro: { stock: ["std", "slim"], order: ["std", "slim", "super"] }
      }
    },
    treatOrder: ["ar", "bl", "photo"],
    planoOrder: ["bl"]
  };

  var T = {}, FRAME = null, ROOT = null, PRICES = null, VARIANT_PRICES = {}, SELLABLE = null, WA = null;
  var STORES = [];
  var S, HIST, inflight = false, lastQuoted = null;
  // when the catalogue was last read, so a tab left open all afternoon does
  // not quote yesterday's price or a lens that has since sold out
  var pricedAt = 0, PRICE_TTL = 300000;
  // the request in flight, so a later one can tell whether the drawer is
  // still its own before it touches anything
  var pending = null;

  function fresh() {
    return {
      screen: "start", path: null, plano: null, treat: null, lab: null,
      slimOpt: false, rxmode: null, storeWhy: null,
      rx: { osph: 0, ocyl: 0, oaxe: "", gsph: 0, gcyl: 0, gaxe: "", pdr: "", pdl: "" }
    };
  }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  }
  function log() {
    if (window.console && console.warn) console.warn.apply(console, arguments);
  }

  /* ---------- formatting ---------- */
  function money(cents) {
    if (typeof cents !== "number" || !isFinite(cents)) return "";
    var fmt = window.MM_MONEY_FORMAT || "";
    var whole = Math.round(cents / 100);
    var grouped;
    /* Shopify's *_with_comma_separator formats use the comma as the DECIMAL
       mark, so the thousands separator is the period (1.200.000). A format that
       names no separator says nothing, and rupiah groups with periods anyway. */
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || "";
    if (fmt.indexOf("comma_separator") !== -1 || cur === "IDR") {
      grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    } else {
      grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    if (fmt.indexOf("{{") !== -1) {
      return fmt.replace(/\{\{\s*[a-z_]+\s*\}\}/i, grouped).replace(/<[^>]*>/g, "").trim();
    }
    return "Rp " + grouped;
  }
  // The lists stop at 6.00. Past that we don't sell online, so the last
  // option on each end is this sentinel: it reads as "beyond our range" and
  // routes the customer to a store appointment.
  var OVER = 99;
  function isOver(v) { return Math.abs(parseFloat(v) || 0) >= OVER; }
  function dio(v) {
    v = parseFloat(v) || 0;
    if (isOver(v)) return v < 0 ? "< -6.00" : "> +6.00";
    return (v < 0 ? "-" : v > 0 ? "+" : "") + Math.abs(v).toFixed(2);
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }
  function fill(str, map) {
    return String(str == null ? "" : str).replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) {
      return map[k] == null ? "" : map[k];
    });
  }

  /* ---------- product data ---------- */
  var cache = {};
  function getProduct(handle) {
    if (cache[handle]) return cache[handle];
    cache[handle] = fetch(root() + "products/" + handle + ".js", { credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status + " on " + handle);
        return r.json();
      })
      .catch(function (e) { delete cache[handle]; throw e; });
    return cache[handle];
  }
  function variantByTitle(p, title) {
    var want = String(title).trim().toLowerCase(), i, v;
    for (i = 0; i < p.variants.length; i++) {
      v = p.variants[i];
      if (String(v.title).trim().toLowerCase() === want) return v;
      if (v.options && String(v.options.join(" / ")).trim().toLowerCase() === want) return v;
    }
    log("[mm-rx] variant not found:", title, "in", p.handle);
    return null;
  }
  function pick(p, title) {
    if (!p || !p.variants) return null;
    var v = variantByTitle(p, title);
    if (!v) return null;
    // Never quote a lens the cart would refuse: /cart/add.js with several items
    // is atomic, so one sold-out lens would reject the frame along with it.
    if (v.available === false) {
      log("[mm-rx] variant unavailable:", title, "in", p.handle);
      return null;
    }
    return { id: v.id, price: v.price, title: v.title };
  }

  // One product missing must not take the whole drawer down with it: each is
  // fetched independently and a failure only removes the branch it feeds.
  function softGet(handle) {
    return getProduct(handle).catch(function (e) {
      log("[mm-rx] product unavailable:", handle, e);
      return null;
    });
  }

  function loadPrices() {
    return Promise.resolve().then(function () {
      var h = CFG.handles;
      return Promise.all([softGet(h.indo), softGet(h.euro), softGet(h.add), softGet(h.plano)]);
    }).then(function (r) {
      var indo = r[0], euro = r[1], add = r[2], plano = r[3];
      if (!indo && !euro && !plano) throw new Error("no lens products could be loaded");
      var out = { lens: {}, thick: {}, plano: {} }, k, lab, bnd;
      for (k in CFG.lens) {
        out.lens[k] = {};
        for (lab in CFG.lens[k]) {
          out.lens[k][lab] = {};
          for (bnd in CFG.lens[k][lab]) {
            out.lens[k][lab][bnd] = pick(lab === "indo" ? indo : euro, CFG.lens[k][lab][bnd]);
          }
        }
      }
      for (k in CFG.thick) out.thick[k] = pick(add, CFG.thick[k]);
      for (k in CFG.plano) out.plano[k] = pick(plano, CFG.plano[k]);
      pricedAt = Date.now();
      return out;
    });
  }

  /* ---------- rules ---------- */
  // The governing power is the strongest value on the prescription itself:
  // the customer enters both eyes in full, and the thickness rules read the
  // stronger of the two.
  function sphMax() { return Math.max(Math.abs(S.rx.osph), Math.abs(S.rx.gsph)); }
  function cylMax() { return Math.max(Math.abs(S.rx.ocyl), Math.abs(S.rx.gcyl)); }
  function sphSigned() {
    return Math.abs(S.rx.osph) >= Math.abs(S.rx.gsph) ? S.rx.osph : S.rx.gsph;
  }
  function cylSigned() {
    return Math.abs(S.rx.ocyl) >= Math.abs(S.rx.gcyl) ? S.rx.ocyl : S.rx.gcyl;
  }
  function rxEntered() { return !!(S.rx.osph || S.rx.ocyl || S.rx.gsph || S.rx.gcyl); }
  function band() { return sphMax() <= CFG.bandMax && cylMax() <= CFG.bandMax ? "stock" : "order"; }
  function tierKey() {
    var p = sphMax();
    if (p >= CFG.ultraFrom) return "ultra";
    if (p >= CFG.superFrom) return "super";
    if (p >= CFG.slimFrom) return "slim";
    return slimTaken() ? "slim" : "std";
  }
  /* The optional Slim counts only while it is actually on screen. Otherwise a
     tick made before an error appeared kept being charged behind a panel that
     no longer showed it. */
  function slimTaken() { return S.slimOpt && slimOfferable() && rxErrors().length === 0; }
  // Beyond this the lens is not sold online at all: it is measured in store.
  // The cylinder counts too, since its list carries the same out-of-range
  // sentinel and a lens we cannot even write down cannot be cut.
  function storeOnly() { return sphMax() > CFG.storeOnlyFrom || cylMax() > CFG.storeOnlyFrom; }
  // below 4.00 a Slim is the customer's choice, for comfort, not a requirement
  function slimOfferable() { return sphMax() < CFG.slimFrom; }
  function slimAdvised() { var p = sphMax(); return p >= CFG.slimAdvisedFrom && p < CFG.slimFrom; }
  function tierLabel(k) { return k === "std" ? "" : (T["tier_" + k] || ""); }

  function thickLine() {
    var k = tierKey();
    return k === "std" ? null : (PRICES ? PRICES.thick[k] : null);
  }
  function lensLine(treat, lab) {
    if (!PRICES || !PRICES.lens[treat] || !PRICES.lens[treat][lab]) return null;
    return PRICES.lens[treat][lab][band()];
  }
  function canMake(treat, lab) {
    var list = CFG.avail[treat] && CFG.avail[treat][lab] && CFG.avail[treat][lab][band()];
    if (!list || list.indexOf(tierKey()) === -1) return false;
    if (!lensLine(treat, lab)) return false;
    // a thinner lens the catalogue can't supply must not be quoted as if it could
    if (tierKey() !== "std" && !thickLine()) return false;
    return true;
  }
  function priceFor(treat, lab) {
    if (!canMake(treat, lab)) return null;
    var base = lensLine(treat, lab), th = thickLine();
    return base.price + (th ? th.price : 0);
  }
  // the cheapest workshop that can really make this lens, or null if neither can
  function bestLab(treat) {
    var labs = ["indo", "euro"], best = null, i, p;
    for (i = 0; i < labs.length; i++) {
      p = priceFor(treat, labs[i]);
      if (p !== null && (best === null || p < best.price)) best = { price: p, lab: labs[i] };
    }
    return best;
  }
  // what this lens costs with and without the optional Slim, cheapest workshop
  function quoteWithSlim(on) {
    var keep = S.slimOpt, b = null;
    S.slimOpt = on;
    // the customer's own choice must be restored even if pricing throws
    try {
      if (S.treat && S.lab && canMake(S.treat, S.lab)) b = { price: priceFor(S.treat, S.lab) };
      else b = S.treat ? bestLab(S.treat) : null;
    } finally { S.slimOpt = keep; }
    return b ? b.price : null;
  }
  function planoLine() { return S.plano && PRICES ? PRICES.plano[S.plano] : null; }

  /* The single source of truth for money. Both the total on screen and the
     lines posted to the cart are derived from this one function, so they
     cannot drift apart. Any condition that removes a line from the cart must
     live here, and nowhere else. */
  function plan() {
    var lines = [];
    // Going to a store means no lens is bought online, and that holds for the
    // plain-lens branch exactly as it does for the prescription branch.
    if (S.path === "plano") {
      if (S.rxmode === "store") return lines;
      // The blue light filter is included with the frame, so it carries no
      // line: it travels as a property on the frame instead. Quoting it at
      // zero and charging zero is the only way both stay true.
      if (S.plano === "bl") return lines;
      var p = planoLine();
      if (p) lines.push({ role: "lens", kind: "plano", v: p });
      return lines;
    }
    /* Nothing is counted until a treatment is chosen: before that the
       customer has picked no lens, so the total is the frame, whatever
       prescription is already typed in behind them. */
    if (S.path === "rx" && S.rxmode !== "store" && S.treat && rxEntered() && !storeOnly()) {
      var th = thickLine();
      if (S.lab) {
        if (!canMake(S.treat, S.lab)) return lines;
        var base = lensLine(S.treat, S.lab);
        if (base) lines.push({ role: "lens", kind: "rx", v: base });
        if (th) lines.push({ role: "lens_option", kind: "thick", v: th });
        return lines;
      }
      /* No workshop chosen yet. Quote the cheapest one that can actually make
         this lens, so the figure on screen is a whole price and never the
         supplement on its own. Choosing the other workshop moves it up. */
      var best = bestLab(S.treat);
      if (best) {
        var prov = lensLine(S.treat, best.lab);
        if (prov) lines.push({ role: "lens", kind: "rx", v: prov });
        if (th) lines.push({ role: "lens_option", kind: "thick", v: th });
      }
    }
    return lines;
  }
  function lensTotal() {
    return plan().reduce(function (sum, l) { return sum + l.v.price; }, 0);
  }
  // price a posted line from the plan, so the pre-flight check reads the
  // payload rather than recomputing the same expression twice
  function priceOfItem(item) {
    var lines = plan(), i;
    for (i = 0; i < lines.length; i++) if (lines[i].v.id === item.id) return lines[i].v.price;
    return NaN;   // an unpriceable line must fail the check, never pass it
  }
  function framePrice() {
    return FRAME && typeof FRAME.price === "number" && isFinite(FRAME.price) ? FRAME.price : 0;
  }
  function grandTotal() { return framePrice() + lensTotal(); }

  function rxErrors() {
    var r = S.rx, e = [];
    if (!r.osph && !r.ocyl && !r.gsph && !r.gcyl) e.push(T.err_empty);
    if (r.ocyl !== 0 && r.oaxe === "") e.push(T.err_axis_right);
    if (r.gcyl !== 0 && r.gaxe === "") e.push(T.err_axis_left);
    var bad = ["pdr", "pdl"].some(function (k) {
      var v = parseFloat(r[k]); return isNaN(v) || v < 25 || v > 38;
    });
    if (bad) e.push(T.err_pd);
    return e;
  }

  /* ---------- markup helpers ---------- */
  function opt(act, label, sub, val, active, dim) {
    var inner = "<span><span class=\"mmrx-opt__lbl\">" + label + "</span>" +
      (sub ? "<span class=\"mmrx-opt__sub\">" + sub + "</span>" : "") + "</span>" +
      (val ? "<span class=\"mmrx-opt__val\">" + val + "</span>" : "");
    if (dim) return '<div class="mmrx-opt mmrx-opt--off">' + inner + "</div>";
    return '<button type="button" class="mmrx-opt" data-act="' + esc(act) + '" aria-pressed="' +
      (active ? "true" : "false") + '">' + inner + "</button>";
  }
  function quiet(act, label) {
    return '<button type="button" class="mmrx-quiet" data-act="' + esc(act) + '">' + label + "</button>";
  }
  function wa(msg) {
    if (!WA) return "";
    var href = "https://wa.me/" + String(WA).replace(/[^0-9]/g, "");
    if (msg) href += "?text=" + encodeURIComponent(msg);
    return '<a class="mmrx-wa" href="' + href + '" target="_blank" rel="noopener">' +
      '<svg class="mmrx-wa__i" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.3 8.6 8.6 0 0 1-4-.9L3 20l1.2-3.4A8.2 8.2 0 0 1 3 11.5' +
      'a8.4 8.4 0 0 1 8.5-8.3 8.4 8.4 0 0 1 9.5 8.3z"/></svg>' + T.wa_button + "</a>";
  }
  // the three stores with an optometrist, kept small and factual
  function storeList() {
    if (!STORES.length) return "";
    var h = '<span class="mmrx-eyebrow mmrx-eyebrow--sp">' + T.stores_label + "</span><ul class=\"mmrx-stores\">";
    STORES.forEach(function (s) {
      if (!s || !s.name) return;
      h += "<li><b>" + esc(s.name) + "</b>";
      // WhatsApp, not a phone call: the number above goes to Canggu, so each
      // store has to reach its own team rather than the flagship's
      if (s.phone) h += '<a href="https://wa.me/' + esc(String(s.phone).replace(/[^0-9]/g, "")) +
        '" target="_blank" rel="noopener">' + esc(s.phone) + "</a>";
      if (s.maps) h += '<a href="' + esc(s.maps) + '" target="_blank" rel="noopener">' + T.maps + "</a>";
      h += "</li>";
    });
    return h + "</ul>";
  }
  function sel(name, vals, cur) {
    var h = '<select data-rx="' + name + '">', i;
    for (i = 0; i < vals.length; i++) {
      h += '<option value="' + vals[i] + '"' +
        (Math.abs(vals[i] - cur) < 0.001 ? " selected" : "") + ">" + dio(vals[i]) + "</option>";
    }
    return h + "</select>";
  }
  function num(name, cur, mn, mx, st, ph) {
    return '<input type="number" data-rx="' + name + '" value="' + esc(cur) + '" min="' + mn +
      '" max="' + mx + '" step="' + st + '"' + (ph ? ' placeholder="' + esc(ph) + '"' : "") + ">";
  }
  function range(min, max, step) {
    var a = [], v;
    for (v = min; v <= max + 1e-6; v += step) a.push(Math.round(v * 100) / 100);
    return a;
  }
  var SPH = [-OVER].concat(range(-6, 6, 0.25)).concat([OVER]);
  var CYL = [-OVER].concat(range(-6, 0, 0.25));

  /* ---------- screens ---------- */
  var SCREENS = {
    start: function () {
      // a branch whose products didn't load is shown as unavailable, never as
      // a button that leads to an empty screen
      var hasPlano = !!(PRICES && PRICES.plano.bl);
      var hasRx = false, tk, lb;
      if (PRICES) {
        for (tk in PRICES.lens) {
          for (lb in PRICES.lens[tk]) {
            if (PRICES.lens[tk][lb].stock || PRICES.lens[tk][lb].order) hasRx = true;
          }
        }
      }
      return {
        step: 1, of: 4, title: T.s1_title, hint: T.s1_hint,
        html: (hasPlano
          ? opt("p-plano", T.s1_plano, T.s1_plano_sub, "", S.path === "plano")
          : opt("", T.s1_plano, T.s1_plano_sub, T.unavailable, false, true)) +
          (hasRx
            ? opt("p-rx", T.s1_rx, T.s1_rx_sub, "", S.path === "rx")
            : opt("", T.s1_rx, T.s1_rx_sub, T.unavailable, false, true)) +
          opt("go-store", T.s1_prog, T.s1_prog_sub, '<span class="mmrx-chip mmrx-chip--warn">' + T.in_store + "</span>", false)
      };
    },

    plano: function () {
      var h = "", i, k, p, any = false;
      for (i = 0; i < CFG.planoOrder.length; i++) {
        k = CFG.planoOrder[i];
        p = PRICES.plano[k];
        if (!p) continue;
        any = true;
        var val = k === "bl"
          ? '<span class="mmrx-free">' + T.included + "<s>" + money(p.price) + "</s></span>"
          : money(p.price);
        h += opt("pl-" + k, T["plano_" + k], T["plano_" + k + "_sub"], val, S.plano === k);
      }
      if (!any) {
        return {
          step: 2, of: 2, title: T.nomake_title, hint: "",
          html: '<div class="mmrx-alert"><span class="mmrx-eyebrow">' + T.nomake_eyebrow + "</span>" + T.nomake_body + "</div>" +
            opt("go-store", T.book_store, T.book_store_sub, '<span class="mmrx-chip mmrx-chip--warn">' + T.in_store + "</span>", false)
        };
      }
      return {
        step: 2, of: 2, title: T.plano_title, hint: T.plano_hint,
        html: h + '<p class="mmrx-note">' + T.plano_note + "</p>",
        cta: (S.plano && !(FRAME && FRAME.sellable === false)) ? { label: T.add_to_cart, act: "add" } : null
      };
    },

    treat: function () {
      var h = "", i, k, l, offered = 0;
      for (i = 0; i < CFG.treatOrder.length; i++) {
        k = CFG.treatOrder[i];
        var lens = PRICES.lens[k] || {};
        var any = ["indo", "euro"].some(function (lb) {
          return lens[lb] && (lens[lb].stock || lens[lb].order);
        });
        if (!any) {   // nothing we can make in either workshop: say so here,
          h += opt("", T["treat_" + k], T["treat_" + k + "_sub"], T.unavailable, false, true);
          continue;   // rather than two screens later
        }
        /* normalize() clears the prescription before this screen is drawn, so
           the figure here is always the catalogue's lowest, never a strong
           correction's price shown to someone who entered nothing final. */
        l = lens.indo ? lens.indo.stock : null;
        if (!l) l = lens.euro ? lens.euro.stock : null;
        offered++;
        h += opt("tr-" + k, T["treat_" + k], T["treat_" + k + "_sub"],
          l ? fill(T.from_price, { price: money(l.price) }) : "", S.treat === k);
      }
      /* Every treatment greyed out. Never leave the customer on a screen whose
         only control is the back arrow: the appointment is the way out. */
      if (!offered) {
        return {
          step: 2, of: 4, title: T.store_only_title, hint: T.store_only_hint,
          html: h + opt("go-store", T.book_store, T.book_store_sub, "", false) + wa()
        };
      }
      return { step: 2, of: 4, title: T.treat_title, hint: T.treat_hint, html: h + '<p class="mmrx-note">' + T.treat_note + "</p>" };
    },

    lab: function () {
      var pi = priceFor(S.treat, "indo"), pe = priceFor(S.treat, "euro"), h = "", tk = tierKey();
      if (pi === null && pe === null) {
        return {
          step: 4, of: 4, title: T.nomake_title,
          hint: fill(T.nomake_hint, { treat: T["treat_" + S.treat], power: sphMax().toFixed(2) }),
          html: '<div class="mmrx-alert"><span class="mmrx-eyebrow">' + T.nomake_eyebrow + "</span>" + T.nomake_body + "</div>" +
            opt("go-store", T.book_store, T.book_store_sub, "", false) + wa()
        };
      }
      var withTier = function (base) {
        return tk === "std" ? base : base + " " + fill(T.tier_included, { tier: tierLabel(tk) });
      };
      h += pi !== null
        ? opt("lab-indo", T.lab_indo, withTier(T.lab_indo_sub), money(pi), S.lab === "indo")
        : opt("", T.lab_indo,
            tk === "std" ? T.lab_indo_off_generic : fill(T.lab_indo_off, { tier: tierLabel(tk), power: sphMax().toFixed(2) }),
            T.unavailable, false, true);
      h += pe !== null
        ? opt("lab-euro", T.lab_euro + '<span class="mmrx-chip">' + (pi === null ? T.only_option : T.most_chosen) + "</span>",
            withTier(T.lab_euro_sub), money(pe), S.lab === "euro")
        : opt("", T.lab_euro, T.lab_euro_off, T.unavailable, false, true);
      h += '<p class="mmrx-note">' + T.lab_note + "</p>";
      var chosen = S.lab && canMake(S.treat, S.lab) && !(FRAME && FRAME.sellable === false);
      return { step: 4, of: 4, title: T.lab_title, hint: T.lab_hint, html: h,
        cta: chosen ? { label: T.add_to_cart, act: "add" } : null };
    },

    rxform: function () {
      var r = S.rx, errs = rxErrors(), tk = tierKey(), h, sl;
      h = '<div class="mmrx-grid"><span></span><span class="mmrx-hd">' + T.sphere + '</span><span class="mmrx-hd">' +
        T.cylinder + '</span><span class="mmrx-hd">' + T.axis + "</span>" +
        '<span class="mmrx-eye">' + T.od + "</span>" + sel("osph", SPH, r.osph) + sel("ocyl", CYL, r.ocyl) + num("oaxe", r.oaxe, 1, 180, 1, "-") +
        '<span class="mmrx-eye">' + T.og + "</span>" + sel("gsph", SPH, r.gsph) + sel("gcyl", CYL, r.gcyl) + num("gaxe", r.gaxe, 1, 180, 1, "-") +
        "</div>" +
        '<p class="mmrx-help">' + T.cyl_sign_help + "</p>" +
        '<div class="mmrx-pd"><span class="mmrx-eye">' + T.pd + "</span>" +
        num("pdr", r.pdr, 25, 38, 0.5, "") +
        num("pdl", r.pdl, 25, 38, 0.5, "") + "<span></span></div>" +
        '<p class="mmrx-help">' + T.pd_help + "</p>";

      // Beyond the online limit nothing else matters: it is an appointment.
      if (rxEntered() && storeOnly()) {
        return {
          step: 3, of: 4, title: T.store_only_title,
          hint: T.store_only_hint,
          html: h + '<div class="mmrx-alert"><span class="mmrx-eyebrow">' + T.store_only_eyebrow + "</span>" +
            T.store_only_body + "</div>" +
            opt("go-store", T.book_store, T.book_store_sub, "", false) + wa()
        };
      }

      /* Neither workshop can make this one. Say so here, on the screen the
         customer is already looking at, instead of letting Continue lead to a
         dead end one screen later. */
      if (!errs.length && rxEntered() && !bestLab(S.treat)) {
        return {
          step: 3, of: 4, title: T.nomake_title,
          hint: fill(T.nomake_hint, { treat: T["treat_" + S.treat], power: sphMax().toFixed(2) }),
          html: h + '<div class="mmrx-alert"><span class="mmrx-eyebrow">' + T.nomake_eyebrow + "</span>" +
            T.nomake_body + "</div>" +
            opt("go-store", T.book_store, T.book_store_sub, "", false) + wa()
        };
      }

      if (errs.length) {
        h += '<div class="mmrx-alert"><span class="mmrx-eyebrow">' + T.to_complete + "</span>" + errs.join("<br><br>") + "</div>";
      } else if (slimOfferable()) {
        // the prescription is complete and a standard lens is enough
        sl = PRICES ? PRICES.thick.slim : null;
        h += '<div class="mmrx-alert mmrx-alert--ok"><span class="mmrx-eyebrow">' + T.std_ok_title + "</span>" +
          fill(slimAdvised() ? T.std_ok_edge : T.std_ok_plain, { power: sphMax().toFixed(2) }) + "</div>";
        /* What the Slim really costs on THIS prescription. On some lenses the
           Bali workshop cannot make a Slim, so the upgrade also moves the order
           to Europe, and the sticker price of the supplement would be a lie. */
        var pNow = quoteWithSlim(false), pSlim = quoteWithSlim(true);
        if (sl && pSlim !== null && pNow !== null) {
          h += '<span class="mmrx-eyebrow mmrx-eyebrow--sp">' + T.slim_offer_title + "</span>" +
            opt("slim-yes", T.tier_slim + (slimAdvised() ? '<span class="mmrx-chip">' + T.advised + "</span>" : ""),
              T.slim_offer_sub, "+ " + money(pSlim - pNow), S.slimOpt === true) +
            opt("slim-no", T.slim_decline, T.slim_decline_sub,
              '<span class="mmrx-free">' + T.included + "</span>", S.slimOpt === false);
        }
      } else {
        h += '<div class="mmrx-alert mmrx-alert--ok"><span class="mmrx-eyebrow">' + T.thin_required_title + "</span>" +
          fill(T.thin_required, { power: sphMax().toFixed(2), tier: tierLabel(tk) }) + "</div>";
      }
      if (!errs.length && band() === "order" && slimOfferable()) {
        h += '<p class="mmrx-note">' + T.band_order + "</p>";
      }

      // a way out for someone who doesn't have their prescription, kept
      // deliberately quiet so it never competes with the form itself
      h += quiet("go-store-unknown", T.peak_unknown);

      return {
        step: 3, of: 4, title: T.rxform_title, hint: T.rxform_hint, html: h,
        cta: errs.length ? { label: T.complete_rx, disabled: true }
                         : { label: T["continue"], act: "go:lab" }
      };
    },

    store: function () {
      return {
        step: HIST.length + 1, of: HIST.length + 1, title: T.store_title, hint: T.store_hint,
        html: '<div class="mmrx-alert"><span class="mmrx-eyebrow">' + T.store_next + "</span>" + T.store_body + "</div>" +
          wa() + storeList(),
        cta: (FRAME && FRAME.sellable === false) ? null : { label: T.frame_only, act: "add" }
      };
    },

    failed: function () {
      return {
        step: 1, of: 1, title: T.cart_error_title, hint: "",
        html: '<div class="mmrx-alert"><span class="mmrx-eyebrow">' + T.cart_error_title + "</span>" +
          T.cart_error_body + "</div>" +
          opt("go-store", T.book_store, T.book_store_sub, '<span class="mmrx-chip">' + T.in_store + "</span>", false)
      };
    }
  };

  /* ---------- render ---------- */
  function q(s) { return ROOT.querySelector(s); }

  /* A choice made earlier must not survive a change that invalidates it.
     Raising the prescription after picking a workshop is the obvious case:
     the workshop is dropped, and the customer is taken back to the
     prescription rather than being quietly re-quoted. */
  function normalize() {
    /* State belonging to a branch the customer has left must never survive
       into the cart. "Go to a store" in particular used to be sticky: it
       silenced the lens price on every later screen while the cart still
       charged for it. */
    if (S.screen === "start") {
      // back at the first screen the customer has chosen nothing yet: the
      // branch goes, and the prescription with it
      S.path = null; S.plano = null; S.treat = null; S.lab = null; S.rxmode = null;
      S.slimOpt = false; S.storeWhy = null;
    }
    /* Back on the treatments screen the customer is choosing again, so the
       previous choice is not theirs any more. Without this the lens stayed in
       the total on a screen where nothing is selected.
       The prescription goes with it: a half filled form must not leave the
       three treatments quoted at a strong correction's price, which reads as
       "this is what I will pay" to someone who has entered nothing final. */
    if (S.screen === "treat") {
      S.treat = null; S.lab = null; S.slimOpt = false;
      S.rx = fresh().rx;
    }
    // and back on the prescription screen the workshop is about to be picked
    // again, so its premium must not sit silently in the total
    if (S.screen === "rxform" && S.lab) S.lab = null;
    if (S.rxmode === "store" && S.screen !== "store") S.rxmode = null;
    if (S.path !== "plano" && S.plano) S.plano = null;
    if (S.path !== "rx" && (S.treat || S.lab)) { S.treat = null; S.lab = null; }

    // a prescription raised past the online limit goes back to the prescription
    // screen, which is where the appointment is offered
    if (S.path === "rx" && storeOnly() && S.screen === "lab") {
      S.lab = null;
      var pk = HIST.lastIndexOf("rxform");
      if (pk !== -1) HIST.length = pk; else HIST = [];
      S.screen = "rxform";
      return;
    }

    if (S.path !== "rx" || !S.treat || !S.lab) return;
    if (canMake(S.treat, S.lab)) return;
    S.lab = null;
  }

  function render() {
    var sc;
    try {
      normalize();
      sc = SCREENS[S.screen]();
    } catch (e) {
      log("[mm-rx] screen failed:", S.screen, e);
      S.screen = "failed";
      sc = SCREENS.failed();
    }

    lastQuoted = grandTotal();

    var body = q("[data-mmrx-body]");
    if (!body) { log("[mm-rx] drawer body missing"); return; }
    var keepScroll = body.scrollTop;
    var active = document.activeElement;
    var keepFocus = active && active.dataset ? active.dataset.rx : null;

    body.innerHTML =
      '<h2 class="mmrx-h2">' + sc.title + "</h2>" +
      (sc.hint ? '<p class="mmrx-hint">' + sc.hint + "</p>" : "") +
      '<div class="mmrx-opts">' + sc.html + "</div>";

    try {
      var stepEl = q("[data-mmrx-step]"), barEl = q("[data-mmrx-bar]"), backEl = q("[data-mmrx-back]");
      var of = sc.of || 4;
      if (stepEl) stepEl.textContent = sc.step + " / " + of;
      if (barEl) barEl.style.width = (sc.step / of * 100) + "%";
      if (backEl) { backEl.disabled = HIST.length === 0; backEl.setAttribute("aria-label", T.back || "Back"); }

      var cta = q("[data-mmrx-cta]");
      if (cta) {
        if (sc.cta) {
          cta.hidden = false;
          var waiting = inflight && sc.cta.act === "add";
          cta.textContent = waiting ? (T.adding || sc.cta.label) : sc.cta.label;
          cta.disabled = !!sc.cta.disabled || waiting;
          cta.dataset.act = sc.cta.act || "";
        } else {
          cta.hidden = true; cta.disabled = false; cta.dataset.act = "";
        }
      }
      var lbl = q("[data-mmrx-total-label]"), tot = q("[data-mmrx-total]");
      if (lbl) lbl.textContent = T.total || "";
      if (tot) tot.textContent = money(lastQuoted);
      ROOT.setAttribute("aria-label", T.aria_label || "");
    } catch (e) {
      log("[mm-rx] chrome update failed:", e);
    }

    if (keepScroll) body.scrollTop = keepScroll;
    if (keepFocus) {
      var again = body.querySelector('[data-rx="' + keepFocus + '"]');
      if (again && again.focus) { try { again.focus(); } catch (e) {} }
    }
  }

  /* The body's contents are replaced on every render, so per-node listeners
     die with them, and a tap that lands after a blur-triggered re-render is
     dispatched on the container, not on the button. Delegating to the
     container, which survives every render, fixes both. */
  function bind() {
    var body = q("[data-mmrx-body]");
    if (!body || body.getAttribute("data-mmrx-delegated") === "1") return;
    body.setAttribute("data-mmrx-delegated", "1");

    body.addEventListener("click", function (e) {
      var b = e.target && e.target.closest ? e.target.closest("[data-act]") : null;
      if (b && body.contains(b)) act(b.getAttribute("data-act"));
    });

    body.addEventListener("change", function (e) {
      var el = e.target && e.target.closest ? e.target.closest("[data-rx]") : null;
      if (!el || !body.contains(el)) return;
      var k = el.dataset.rx, v = el.value;
      if (k === "oaxe" || k === "gaxe") {
        var ax = parseInt(v, 10);
        // an axis is 1 to 180; anything else is not an axis, so treat it as blank
        S.rx[k] = (v === "" || isNaN(ax) || ax < 1 || ax > 180) ? "" : String(ax);
      } else if (k === "pdr" || k === "pdl") {
        S.rx[k] = v === "" ? "" : parseFloat(v);
      } else {
        var n = parseFloat(v);
        S.rx[k] = isNaN(n) ? 0 : n;
        // an axis only means something alongside a cylinder
        if (k === "ocyl" && S.rx.ocyl === 0) S.rx.oaxe = "";
        if (k === "gcyl" && S.rx.gcyl === 0) S.rx.gaxe = "";
      }
      render();
    });
  }

  /* Which door led to the appointment. The optician needs it: a progressive
     and a prescription past our online range are not the same visit. */
  function whyStore() {
    if (S.screen === "start") return "progressive lenses";
    if (S.screen === "lab") return "combination not made online";
    if (S.screen === "rxform") {
      if (storeOnly()) return "prescription beyond 6.00";
      if (!rxEntered()) return "prescription unknown";
      if (S.treat && !bestLab(S.treat)) return "combination not made online";
      return "measured in store";
    }
    return "in store";
  }
  function go(next) {
    if (!SCREENS[next]) { log("[mm-rx] unknown screen", next); return; }
    HIST.push(S.screen);
    S.screen = next;
    render();
  }

  function act(a) {
    if (!a) return;
    try {
      // a single lens on this branch, so it is chosen for them and the
      // screen is a confirmation rather than a choice
      if (a === "p-plano") { S.path = "plano"; S.plano = "bl"; return go("plano"); }
      if (a === "p-rx") { S.path = "rx"; return go("treat"); }
      if (a === "go-store") { S.rxmode = "store"; S.storeWhy = whyStore(); return go("store"); }
      // the quiet link, which says something the screen state cannot
      if (a === "go-store-unknown") { S.rxmode = "store"; S.storeWhy = "prescription unknown"; return go("store"); }
      if (a.indexOf("pl-") === 0) { S.plano = a.slice(3); return render(); }
      if (a.indexOf("tr-") === 0) { S.treat = a.slice(3); S.rxmode = "now"; return go("rxform"); }
      if (a === "slim-yes") { S.slimOpt = true; return render(); }
      if (a === "slim-no") { S.slimOpt = false; return render(); }
      if (a.indexOf("lab-") === 0) { S.lab = a.slice(4); return render(); }
      if (a.indexOf("go:") === 0) {
        var target = a.slice(3);
        var at = HIST.lastIndexOf(target);
        if (at !== -1) { HIST.length = at; S.screen = target; return render(); }
        return go(target);
      }
      if (a === "add") return addToCart();
    } catch (e) {
      log("[mm-rx] action failed:", a, e);
      S.screen = "failed";
      render();
    }
  }

  /* Line item properties the product form already carries, the Handmade
     "Frame size" above all. The drawer posts its own payload, so without this
     the workshop would receive a made to order frame with no size on it. */
  function formProps() {
    var out = {}, form = document.querySelector('form[action*="/cart/add"]');
    if (!form || !form.querySelectorAll) return out;
    Array.prototype.forEach.call(form.querySelectorAll('[name^="properties["]'), function (f) {
      if ((f.type === "radio" || f.type === "checkbox") && !f.checked) return;
      var m = f.name.match(/^properties\[(.+)\]$/);
      if (m && f.value !== "") out[m[1]] = f.value;
    });
    return out;
  }

  /* ---------- cart ---------- */
  function buildItems() {
    var bid = "mm_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    var lines = plan();
    var frameProps = { _bundle_id: bid, _bundle_role: "frame" };
    var carried = formProps(), ck;
    for (ck in carried) frameProps[ck] = carried[ck];

    // An order to be glazed in store carries no lens line, so everything the
    // optician needs has to travel on the frame itself, otherwise it arrives
    // indistinguishable from a plain frame purchase.
    if (S.rxmode === "store") {
      frameProps._fulfilment = "lenses fitted in store";
      if (S.storeWhy) frameProps._store_reason = S.storeWhy;
      if (S.treat) frameProps._lens_wanted = T["treat_" + S.treat] || S.treat;
      if (S.lab) frameProps._lens_lab_wanted = S.lab === "indo" ? "Bali" : "Europe";
      // the prescription travels only if it still belongs to this order: an
      // abandoned branch must not put single vision values on a progressive
      if (S.path === "rx" && S.treat && rxEntered()) {
        frameProps._rx_peak = "SPH " + dio(sphSigned()) + " / CYL " + dio(cylSigned());
      }
    }

    // the included blue light filter has no line of its own, so the workshop
    // learns about it from the frame
    if (S.path === "plano" && S.plano) {
      frameProps._lens_type = "plano";
      frameProps._lens_finish = T["plano_" + S.plano] || S.plano;
      if (S.plano === "bl") frameProps._lens_included = "blue light filter, included with the frame";
    }

    // Shopify hides every property whose name starts with an underscore, so
    // none of the above reaches the customer. Where the frame is the only line
    // in the cart, one visible line has to say what the lenses are, otherwise
    // an optical frame reads as a pair of sunglasses.
    var shown = null;
    if (S.rxmode === "store") shown = T.prop_store;
    else if (S.path === "plano" && S.plano === "bl") shown = T.prop_bl;
    if (shown) frameProps[T.prop_lenses || "Lenses"] = shown;

    var items = [{ id: FRAME.id, quantity: 1, properties: frameProps }];

    lines.forEach(function (l) {
      var props;
      if (l.kind === "plano") {
        props = { _bundle_id: bid, _bundle_role: "lens", _lens_type: "plano",
          _lens_finish: T["plano_" + S.plano] || S.plano };
      } else if (l.kind === "rx") {
        props = {
          _bundle_id: bid, _bundle_role: "lens", _lens_type: "single_vision",
          _lens_lab: S.lab === "indo" ? "Bali" : "Europe",
          _lens_band: band() === "order" ? "surfaced to prescription" : "stock",
          // the full prescription is required before any online order,
          // so it always travels with the lens
          _rx_source: "customer_form",
          _rx_status: "complete",
          _rx_peak: "SPH " + dio(sphSigned()) + " / CYL " + dio(cylSigned()),
          _rx_right: "SPH " + dio(S.rx.osph) + " / CYL " + dio(S.rx.ocyl) +
            " / AXIS " + (S.rx.ocyl !== 0 && S.rx.oaxe !== "" ? S.rx.oaxe : "n/a"),
          _rx_left: "SPH " + dio(S.rx.gsph) + " / CYL " + dio(S.rx.gcyl) +
            " / AXIS " + (S.rx.gcyl !== 0 && S.rx.gaxe !== "" ? S.rx.gaxe : "n/a"),
          _rx_pd: "R " + S.rx.pdr + " / L " + S.rx.pdl
        };
      } else {
        props = { _bundle_id: bid, _bundle_role: "lens_option" };
        if (slimTaken() && tierKey() === "slim") props._chosen = "customer asked for a thinner lens";
        else props._auto_applied = "sphere " + dio(sphSigned());
      }
      items.push({ id: l.v.id, quantity: 1, properties: props });
    });
    return items;
  }

  function showCartError() {
    var body = q("[data-mmrx-body]");
    if (!body) return;
    var old = body.querySelector("[data-mmrx-cart-error]");
    if (old) old.parentNode.removeChild(old);
    var d = document.createElement("div");
    d.className = "mmrx-alert";
    d.setAttribute("data-mmrx-cart-error", "1");
    d.innerHTML = '<span class="mmrx-eyebrow">' + T.cart_error_title + "</span>" + T.cart_error_body;
    body.appendChild(d);
    if (d.scrollIntoView) { try { d.scrollIntoView({ block: "nearest" }); } catch (e) {} }
  }

  function addToCart() {
    if (inflight) return;
    var cta = q("[data-mmrx-cta]");
    var items;

    try {
      if (!FRAME || !FRAME.id || isNaN(FRAME.id)) throw new Error("no frame variant");
      if (FRAME.sellable === false) throw new Error("frame variant is sold out");

      // Submit-time gate. Every guard elsewhere is a render-time guard; this
      // is the one that runs on the payload we are actually about to post.
      normalize();
      if (S.path === "rx" && S.rxmode !== "store" && storeOnly()) {
        log("[mm-rx] beyond the online limit, appointment only");
        render(); showCartError();
        return;
      }
      if (S.path === "rx" && S.rxmode !== "store" && !(S.treat && S.lab && canMake(S.treat, S.lab))) {
        log("[mm-rx] refusing to post an unmakeable combination");
        render(); showCartError();
        return;
      }
      if (S.path === "rx" && S.rxmode !== "store" && rxErrors().length) {
        log("[mm-rx] refusing to post an incomplete prescription");
        render(); showCartError();
        return;
      }
      if (S.path === "plano" && !planoLine() && S.plano !== "bl") {
        log("[mm-rx] refusing to post a plano lens with no variant");
        render(); showCartError();
        return;
      }

      items = buildItems();
    } catch (e) {
      log("[mm-rx] cannot build cart payload:", e);
      S.screen = "failed";
      render();
      return;
    }

    /* The real guard: what we are about to post, priced from the payload
       itself, must equal the figure the customer last saw on screen. If they
       differ, the state moved after the total was drawn, so we re-quote and
       let them see the new number instead of charging it silently. */
    var charged = items.reduce(function (s, i) {
      return s + (i.id === FRAME.id ? framePrice() : priceOfItem(i));
    }, 0);
    if (lastQuoted !== null && charged !== lastQuoted) {
      log("[mm-rx] quote", lastQuoted, "!= charge", charged, ", re-quoting instead of posting");
      render(); showCartError();
      return;
    }

    inflight = true;
    var ctl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (pending && pending.ctl !== ctl) return;   // a later request owns the drawer now
      if (!inflight) return;
      // give up on this one, and say so, rather than waiting for ever
      if (ctl) ctl.abort();
      inflight = false;
      if (body) body.removeAttribute("aria-busy");
      try { render(); showCartError(); } catch (e) {}
    }, 20000);
    pending = { ctl: ctl, timer: timer };
    var body = q("[data-mmrx-body]");
    if (body) body.setAttribute("aria-busy", "true");
    if (cta) { cta.disabled = true; cta.hidden = false; cta.textContent = T.adding; }

    var opts = {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ items: items })
    };
    if (ctl) opts.signal = ctl.signal;

    fetch(root() + "cart/add.js", opts).then(function (r) {
      clearTimeout(timer);
      pending = null;
      if (!r.ok) return r.text().then(function (t) { throw new Error(t); });
      // The item is in the cart now. Even if the drawer was closed while the
      // request was in flight, showing them the cart is honest; leaving a
      // line they cannot see is not.
      window.location.href = root() + "cart";
    }).catch(function (e) {
      clearTimeout(timer);
      pending = null;
      if (e && e.name === "AbortError") return;   // cancelled, nothing to report
      inflight = false;
      if (body) body.removeAttribute("aria-busy");
      log("[mm-rx] add to cart failed:", e);
      try { render(); } catch (err) {}
      showCartError();
    });
  }

  /* ---------- open / close ---------- */
  function open() {
    S = fresh(); HIST = []; lastQuoted = null;
    /* A drawer closed mid-request used to come back with every button stuck on
       "Adding...", navigation included, until the timeout fired. It is not
       cancelled here either: a request already sent may have reached Shopify,
       and cancelling it so the customer can press again is how one order
       becomes two. Only the add button waits; the rest of the drawer moves. */
    var b0 = ROOT.querySelector("[data-mmrx-body]");
    if (b0 && !inflight) b0.removeAttribute("aria-busy");
    ROOT.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("mmrx-open");
    try {
      render();
      var first = ROOT.querySelector("[data-mmrx-body] [data-act], [data-mmrx-body] [data-rx]");
      if (first && first.focus) { try { first.focus(); } catch (e) {} }
    } catch (e) {
      log("[mm-rx] render failed on open:", e);
      close();
    }
  }
  function close() {
    ROOT.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("mmrx-open");
  }

  /* ---------- init ---------- */
  var globalsBound = false, openerBound = false, OWN = null;
  // FRAME is mutated as the customer switches variant, so every frame we keep
  // for later has to be a copy, never the object the data block handed us
  function frameCopy(f) {
    return f ? { id: f.id, title: f.title, price: f.price, sellable: f.sellable } : null;
  }

  function init() {
    initFrom(document.getElementById("mm-rx"));
  }

  function initFrom(node) {
    if (!node) return;
    if (node === ROOT && node.getAttribute("data-mmrx-ready") === "1") { bindOpeners(); return; }
    // A section re-render inserts a fresh drawer; discard the one we moved to
    // <body> last time so we never leave two nodes with the same id.
    if (ROOT && ROOT !== node) {
      // the outgoing drawer may be open: never leave the page scroll locked
      // with no drawer on screen
      try { close(); } catch (e) {}
      if (ROOT.parentNode) ROOT.parentNode.removeChild(ROOT);
    }
    ROOT = node;

    /* Bind the ways OUT before anything that can fail. If the config is
       missing or broken we return early, and the delegated open button is
       still live from a previous init, so a drawer with no close control
       would leave a phone with no way back to the page. */
    Array.prototype.forEach.call(ROOT.querySelectorAll("[data-mmrx-close]"), function (b) {
      b.addEventListener("click", close);
    });
    var backEl0 = q("[data-mmrx-back]");
    if (backEl0) backEl0.addEventListener("click", function () {
      if (HIST && HIST.length) { S.screen = HIST.pop(); render(); }
    });

    // Read the config from this drawer's own section: the previous drawer was
    // moved to <body>, so a document-wide lookup could pair this drawer with
    // another product's data block.
    var scope = (node.closest && node.closest('[id^="shopify-section"]')) || node.parentNode || document;
    var cfgEl = (scope.querySelector && scope.querySelector("#mm-rx-data")) ||
                document.getElementById("mm-rx-data");
    if (!cfgEl) { log("[mm-rx] no config block"); return; }
    var data;
    try {
      data = JSON.parse(cfgEl.textContent);
    } catch (e) {
      log("[mm-rx] config JSON is invalid:", e);
      return;
    }
    T = data.t || {};
    FRAME = frameCopy(data.frame) || {};
    VARIANT_PRICES = data.variants || {};
    SELLABLE = data.sellable || null;
    if (FRAME.id != null && FRAME.sellable == null) {
      FRAME.sellable = !SELLABLE || SELLABLE.indexOf(String(FRAME.id)) !== -1;
    }
    WA = data.whatsapp || null;
    STORES = data.stores || [];
    OWN = data;   // this drawer's own frame, to fall back on

    // position:fixed must resolve against the viewport, never against a
    // transformed ancestor of the product form.
    if (ROOT.parentNode !== document.body) document.body.appendChild(ROOT);
    // any other copy of the drawer on the page is a duplicate id; the one on
    // <body> serves every product form, re-framed on click
    Array.prototype.forEach.call(document.querySelectorAll("#mm-rx"), function (n2) {
      if (n2 !== ROOT && n2.parentNode) n2.parentNode.removeChild(n2);
    });

    var form = document.querySelector('form[action*="/cart/add"]');
    var idInput = form ? form.querySelector('[name="id"]') : null;
    function syncVariant(v) {
      var n = parseInt(v, 10);
      if (!n || isNaN(n)) return;
      // Only follow a variant we know the price of, otherwise it belongs to
      // another form on the page, and following it would quote one variant
      // while charging another.
      if (VARIANT_PRICES[n] == null) return;
      FRAME.id = n;
      FRAME.price = VARIANT_PRICES[n];
      FRAME.sellable = !SELLABLE || SELLABLE.indexOf(String(n)) !== -1;
      if (ROOT.getAttribute("aria-hidden") === "false") render();
    }
    if (idInput) {
      syncVariant(idInput.value);
      idInput.addEventListener("change", function () { syncVariant(idInput.value); });
      if (form) {
        form.addEventListener("change", function () {
          var live = form.querySelector('[name="id"]');
          if (live) syncVariant(live.value);
        });
      }
    }

    Array.prototype.forEach.call(ROOT.querySelectorAll("[data-mmrx-close]"), function (b) {
      if (b.setAttribute) b.setAttribute("aria-label", T.close || "Close");
    });
    var ctaEl = q("[data-mmrx-cta]");
    if (ctaEl) ctaEl.addEventListener("click", function () { act(this.dataset.act); });
    bind();

    if (!globalsBound) {
      globalsBound = true;
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && ROOT && ROOT.getAttribute("aria-hidden") === "false") {
          e.stopPropagation();   // don't also close the theme's own drawers
          close();
        }
      });
      window.addEventListener("pagehide", function () { if (ROOT) close(); });
      // A page restored from the back-forward cache does not re-run this
      // script. Without this the drawer would come back with a permanently
      // disabled "Adding…" button after any successful add.
      window.addEventListener("pageshow", function () {
        inflight = false;
        var b = ROOT && ROOT.querySelector("[data-mmrx-body]");
        if (b) b.removeAttribute("aria-busy");
      });
      document.addEventListener("shopify:section:load", init);
    }

    ROOT.setAttribute("data-mmrx-ready", "1");
    bindOpeners();
  }

  // A theme can render the product form more than once on a page (the main
  // product plus a featured-product section). There is only ever one drawer,
  // so take the frame from the section the clicked button lives in, otherwise
  // the second button would quote the first product.
  function adoptFrameFrom(btn) {
    /* Falling back to this drawer's own frame matters: without it, a button
       whose section carries no data block would quote, and charge, whichever
       product was opened before it. */
    function fallback() {
      if (!OWN || !OWN.frame) return;
      FRAME = frameCopy(OWN.frame);
      VARIANT_PRICES = OWN.variants || {};
      SELLABLE = OWN.sellable || null;
      if (OWN.t) T = OWN.t;
      if (OWN.whatsapp) WA = OWN.whatsapp;
      if (OWN.stores) STORES = OWN.stores;
      // and follow whatever variant that form is showing right now
      var own = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (own) {
        var n0 = parseInt(own.value, 10);
        if (n0 && VARIANT_PRICES[n0] != null) {
          FRAME.id = n0;
          FRAME.price = VARIANT_PRICES[n0];
          FRAME.sellable = !SELLABLE || SELLABLE.indexOf(String(n0)) !== -1;
        }
      }
    }
    if (!btn.closest) return fallback();
    var scope = btn.closest('[id^="shopify-section"]') || btn.closest("product-form");
    if (!scope || !scope.querySelector) return fallback();
    var cfg = scope.querySelector("#mm-rx-data, [data-mm-rx-data]");
    if (!cfg) return fallback();
    var data;
    try { data = JSON.parse(cfg.textContent); } catch (e) { log("[mm-rx] bad config on click:", e); return fallback(); }
    if (!data || !data.frame || !data.frame.id) return fallback();
    if (!FRAME || data.frame.id !== FRAME.id) {
      FRAME = frameCopy(data.frame);
      VARIANT_PRICES = data.variants || {};
      SELLABLE = data.sellable || null;
      if (data.whatsapp) WA = data.whatsapp;
      if (data.stores) STORES = data.stores;
      if (data.t) T = data.t;
      if (FRAME && FRAME.id != null && FRAME.sellable == null) {
        FRAME.sellable = !SELLABLE || SELLABLE.indexOf(String(FRAME.id)) !== -1;
      }
    }
    /* The variant picker is read every time, not only when the product
       changes: the section may be showing another colour than the one its
       data block was rendered with. */
    var live = scope.querySelector('form[action*="/cart/add"] [name="id"]');
    if (live) {
      var n = parseInt(live.value, 10);
      if (n && VARIANT_PRICES[n] != null) {
        FRAME.id = n;
        FRAME.price = VARIANT_PRICES[n];
        FRAME.sellable = !SELLABLE || SELLABLE.indexOf(String(n)) !== -1;
      }
    }
  }

  /* The product section is re-rendered by the theme editor, by variant
     pickers and by quick-add drawers, and some of those CLONE the existing
     markup rather than fetching fresh HTML. Either way the button that ends
     up on the page may carry no listener, the dead button that was reported.
     One delegated listener on the document survives all of it, no matter how
     the button got there. */
  function bindOpeners() {
    if (openerBound) return;
    openerBound = true;
    document.addEventListener("click", function (e) {
      var b = e.target && e.target.closest ? e.target.closest("[data-mmrx-open]") : null;
      if (!b || b.disabled) return;

      adoptFrameFrom(b);

      if (!ROOT || !ROOT.isConnected) return;   // let the click fall through
      e.preventDefault();
      if (PRICES && Date.now() - pricedAt < PRICE_TTL) { open(); return; }
      cache = {};   // read the catalogue again, prices and stock both
      b.disabled = true;
      Promise.resolve().then(loadPrices).then(function (p) {
        PRICES = p; b.disabled = false; open();
      }).catch(function (err2) {
        b.disabled = false;
        log("[mm-rx] price load failed:", err2);
        // a refresh that fails is not a reason to refuse the sale: the prices
        // already in hand are worth more than an error screen
        if (PRICES) { open(); return; }
        S = fresh(); HIST = []; S.screen = "failed";
        ROOT.setAttribute("aria-hidden", "false");
        document.documentElement.classList.add("mmrx-open");
        try { render(); } catch (err) { close(); }
      });
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
